/**
 * Transform-patch builder + committer. `buildObjectTransformPatch`
 * produces an `AppState` partial that applies an object/collection/
 * blendFile transform update across all three target kinds with a
 * single adapter; `applyObjectTransformPatch` commits it and refreshes
 * any dependent generators so cables and other world-space rigs stay
 * pinned to the moved geometry.
 *
 * This is the ONE place drag / gizmo / command-buffer paths should
 * write transform state through — it owns the generator-rebuild hand-
 * off. Callers that skip it must manage their own set cycle AND their
 * own generator rebuild.
 */

import type { GeneratorTarget } from "../storeGeneratorOps.ts";
import type { AppState } from "../storeTypes.ts";
import type { SnapshotTransformTarget } from "./transformMath.ts";

type Set_ = (partial: Partial<AppState>) => void;
type Get_ = () => AppState;

type TransformPatch = {
  position?: [number, number, number];
  rotationEuler?: [number, number, number];
  rotationQuat?: [number, number, number, number];
  scale?: [number, number, number];
};

/**
 * Produce the `AppState` slice that applies `updateTarget` to every
 * entry in `targets`. Handles the three-way split between scene
 * objects (Euler rotation, direct fields), collections (Quat rotation,
 * nested `transform`), and blendFiles (Quat rotation, nested
 * `groupTransform`) so callers don't replicate the shape-matching.
 */
export function buildObjectTransformPatch(
  state: AppState,
  targets: ReadonlyArray<SnapshotTransformTarget>,
  updateTarget: (target: SnapshotTransformTarget) => TransformPatch,
): Partial<AppState> {
  const nextSceneObjects = new Map(state.sceneObjects);
  const nextBlendFiles = new Map(state.blendFiles);
  const nextCollections = new Map(state.collections);

  for (const target of targets) {
    const next = updateTarget(target);
    if (target.kind === "scene") {
      const sceneObject = state.sceneObjects.get(target.id);
      if (!sceneObject) continue;
      // World-space generators (e.g. cable) own their geometry in
      // absolute coordinates and must stay pinned to the origin — drag /
      // gizmo writes to the owner are silently dropped rather than
      // letting the transform drift out from under the geometry.
      if (sceneObject.generator?.worldSpace) continue;
      nextSceneObjects.set(target.id, {
        ...sceneObject,
        position: next.position ?? sceneObject.position,
        rotation: next.rotationEuler ?? sceneObject.rotation,
        scale: next.scale ?? sceneObject.scale,
      });
      continue;
    }

    if (target.kind === "collection") {
      const collection = state.collections.get(target.id);
      if (!collection) continue;
      nextCollections.set(target.id, {
        ...collection,
        transform: {
          ...collection.transform,
          position: next.position ?? collection.transform.position,
          rotation: next.rotationQuat ?? collection.transform.rotation,
          scale: next.scale ?? collection.transform.scale,
        },
      });
      continue;
    }

    const entry = state.blendFiles.get(target.id);
    if (!entry) continue;
    nextBlendFiles.set(target.id, {
      ...entry,
      groupTransform: {
        ...entry.groupTransform,
        position: next.position ?? entry.groupTransform.position,
        rotation: next.rotationQuat ?? entry.groupTransform.rotation,
        scale: next.scale ?? entry.groupTransform.scale,
      },
    });
  }

  return {
    sceneObjects: nextSceneObjects,
    collections: nextCollections,
    blendFiles: nextBlendFiles,
  };
}

/**
 * Commit a transform patch produced by `buildObjectTransformPatch` and then
 * refresh any generators that depend on the moved scene objects. This is the
 * single, centralized seam for "transform change → live generator rebuild"
 * on the drag / gizmo / command-buffer paths; direct callers of
 * `set(buildObjectTransformPatch(...))` are rare (only low-level paths that
 * manage their own set cycle) — everything the user can drag should route
 * through here so cables, etc. stay in sync.
 *
 * `dependentTargets` (optional) lets the live drag path bypass the
 * per-target `rebuildDependentGenerators` scan by passing a pre-resolved
 * list cached on `commandBuffer.dependentRebuildTargets` at command-enter.
 * Confirm/programmatic callers omit it and pay the scan once.
 */
export function applyObjectTransformPatch(
  set: Set_,
  get: Get_,
  state: AppState,
  targets: ReadonlyArray<SnapshotTransformTarget>,
  updateTarget: (target: SnapshotTransformTarget) => TransformPatch,
  dependentTargets?: ReadonlyArray<GeneratorTarget> | null,
): void {
  set(buildObjectTransformPatch(state, targets, updateTarget));
  const store = get();
  if (dependentTargets) {
    store.scheduleDependentRebuildTargets(dependentTargets);
    return;
  }
  for (const target of targets) {
    if (target.kind === "scene") {
      store.rebuildDependentGenerators(target.id);
    } else if (target.kind === "collection") {
      const collection = store.collections.get(target.id);
      for (const objectId of collection?.objectIds ?? []) {
        store.rebuildDependentGenerators(objectId);
      }
    }
  }
}
